import { useCallback, useEffect, useState } from 'react'
import { Coachmark } from 'coachmark'
import { useGlobalEvent } from '@highlight/ipc'
import { setOverlayInteractive } from '@highlight/ui'
import { buildMeetingTourSteps, type MeetingTourStepDescriptor } from './build-meeting-tour-steps'
import { useSetMeetingTourPromptSlot } from './prompt-slot'
import { getTourAnchor, tourTargetElement } from './tour-anchor'
import { useMeetingTourPrompt } from './use-meeting-tour-prompt'
import { useMeetingTourStatus, type MeetingTourStatus } from './meeting-tour-status'
import { TourStepCard } from './nested/tour-step-card'
import type { MeetingViewModel } from '../../../views/meeting-view/use-meeting-view-model'

interface MeetingTourProps {
  model: MeetingViewModel
}

/**
 * Guided tour of the Meeting View (HIG-6069 spike), built on the `coachmark`
 * package. Offered once, after the user's first meeting ends (see
 * `useMeetingTourPrompt`); walks the post-meeting landmarks with a spotlight
 * cutout and a step card. Mounted by `MeetingView` so it lives and dies with
 * the view; navigating away cancels the tour.
 */
export function MeetingTour({ model }: MeetingTourProps) {
  const { promptVisible, declinePrompt, hidePrompt } = useMeetingTourPrompt(model)
  const setPromptSlot = useSetMeetingTourPromptSlot()
  const [, setStatus] = useMeetingTourStatus()
  const [tourOpen, setTourOpen] = useState(false)
  const [steps, setSteps] = useState<Array<MeetingTourStepDescriptor>>([])

  const startTour = useCallback(() => {
    hidePrompt()
    // Resolved against the DOM at open time: a meeting with no follow-ups
    // anchors no For You content to point at, and an open notepad leaves no
    // chat input. A step whose target is absent is dropped, not shown empty.
    setSteps(buildMeetingTourSteps().filter((step) => tourTargetElement(step.target)))
    setTourOpen(true)
  }, [hidePrompt])

  // Dev entry (settings ▸ developers): skips the prompt and every first-meeting
  // check and starts the tour outright. It still needs a past meeting under it
  // to point at.
  const forceStartTour = useCallback(() => {
    if (model.variant === 'past') startTour()
  }, [model.variant, startTour])
  useGlobalEvent('meetingTour:start', forceStartTour)

  const endTour = useCallback(
    (outcome: MeetingTourStatus) => {
      setStatus(outcome)
      setTourOpen(false)
    },
    [setStatus],
  )

  // The tour only ends through its own buttons. `open` is pinned true below,
  // so any close Base UI initiates reopens on its own unless it came from the
  // Skip button (a Popover.Close, reason 'close-press'). Outside clicks and
  // Escape land here with their own reasons and are ignored.
  const handleOpenChange = useCallback(
    (open: boolean, details: { reason?: string }) => {
      if (open) return
      if (details?.reason === 'close-press') endTour('dismissed')
    },
    [endTour],
  )

  const handleFinish = useCallback(() => endTour('completed'), [endTour])

  // Published rather than rendered here: the offer belongs above the chat
  // input, which is a different subtree. The cleanup covers both answering the
  // offer and leaving the meeting mid-offer.
  useEffect(() => {
    if (!promptVisible) return
    setPromptSlot({ onAccept: startTour, onDecline: declinePrompt })
    return () => setPromptSlot(null)
  }, [promptVisible, startTour, declinePrompt, setPromptSlot])

  // The popup and backdrop portal outside the panel, so the overlay window has
  // to accept clicks beyond the panel's own bounds, the same arrangement the
  // QuickTour makes for its card.
  useEffect(() => {
    if (!tourOpen) return
    setOverlayInteractive(true)
    return () => setOverlayInteractive(false)
  }, [tourOpen])

  if (model.variant !== 'past') return null

  return (
    <>
      {tourOpen && steps.length > 0 && (
        // `open` pinned true: closing happens by unmounting (Done, Skip), never
        // by the popover's own dismissal, which handleOpenChange filters. The
        // step index stays uncontrolled; controlling it made the library's
        // async step transition commit against a stale index and killed the
        // popup mid-advance.
        <Coachmark.Root open defaultStepIndex={0} onOpenChange={handleOpenChange}>
          <Coachmark.Backdrop className="bg-backdrop fixed inset-0 z-[100] transition-[clip-path] duration-300 ease-out motion-reduce:transition-none" />
          {steps.map((step) => (
            <Coachmark.Step
              key={step.target}
              target={getTourAnchor(step.target)}
              spotlightPadding={6}
              spotlightRadius={16}
            >
              <TourStepCard step={step} onFinish={handleFinish} />
            </Coachmark.Step>
          ))}
        </Coachmark.Root>
      )}
    </>
  )
}
